// apps/web/src/features/russia-requests/components/editor/RussiaEditorBasicsCard.tsx
//
// Type + name card for the russia-rates manual editor. Mirrors the
// china `DraftBasicsCard` composition pattern — keeps form-field wiring
// next to its label so the host stays a thin composition surface.

import { useMemo } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RussiaRequestType, type CreateRussiaRequestManualInput } from '@magimp/shared';
import { Select } from '../../../../ui/index.js';
import { Card, Field, inputStyle } from './EditorPrimitives.js';

const TYPE_OPTIONS = [
  RussiaRequestType.LEAD,
  RussiaRequestType.WAREHOUSE,
  RussiaRequestType.IN_TRANSIT,
  RussiaRequestType.URGENT,
] as const;

function typeToKey(tp: RussiaRequestType): 'lead' | 'warehouse' | 'inTransit' | 'urgent' {
  switch (tp) {
    case RussiaRequestType.LEAD:
      return 'lead';
    case RussiaRequestType.WAREHOUSE:
      return 'warehouse';
    case RussiaRequestType.IN_TRANSIT:
      return 'inTransit';
    case RussiaRequestType.URGENT:
      return 'urgent';
  }
}

export interface RussiaEditorBasicsCardProps {
  form: UseFormReturn<CreateRussiaRequestManualInput>;
  onChanged: () => void;
}

export function RussiaEditorBasicsCard({ form, onChanged }: RussiaEditorBasicsCardProps) {
  const { t } = useTranslation('russiaRequests');
  const {
    control,
    formState: { errors },
  } = form;
  // WHY: `register('name')` returns a fresh `{ref, onChange, onBlur,
  //   name}` each call, and React reconciles a new `ref` callback by
  //   detaching + reattaching the DOM node. Memoizing per form
  //   instance keeps the input ref stable across renders, which keeps
  //   focus during `userEvent.type` and prevents per-keystroke drop.
  const nameRegister = useMemo(() => {
    const reg = form.register('name');
    return {
      ...reg,
      onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
        const out = await reg.onChange(e);
        onChanged();
        return out;
      },
    };
  }, [form, onChanged]);

  const nameError = errors.name ? t('editor.errors.nameRequired') : null;

  return (
    <Card title={t('editor.cards.basics')}>
      <Field label={t('editor.fields.type')}>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(v) => {
                field.onChange(v as RussiaRequestType);
                onChanged();
              }}
              options={TYPE_OPTIONS.map((tp) => ({
                value: tp,
                label: t(`types.${typeToKey(tp)}`),
              }))}
            />
          )}
        />
      </Field>

      <Field label={t('editor.fields.name')} htmlFor="russia-editor-name" error={nameError}>
        <input
          id="russia-editor-name"
          data-testid="russia-requests-editor-name"
          type="text"
          maxLength={200}
          aria-invalid={errors.name ? true : undefined}
          style={inputStyle}
          {...nameRegister}
        />
      </Field>
    </Card>
  );
}
